// LA CONDOTTA del Burraco, per dire PERCHE un tavolo in classifica e rifiutato (I-762, il messaggio di SOSPENSIONE): la forma della risposta del BFF di Limulo
// (`GET /v1/games/burraco/condotta/mine`, endpoint `qlash.games.burraco.condotta.read`, capability `burraco.condotta.read`) e la frase che ne esce.
// Funzioni PURE, niente rete: la query sta in `services/limuloApi.ts` (`getMyBurracoCondotta`), il consumatore in `components/burraco/GiocaBurraco.tsx`.
//
// PERCHE si rilegge invece di leggere il 403: sul `POST /v1/games/burraco/tables` il game host risponde `403 { error: "SUSPENDED", … }`, ma il BFF di
// Limulo NON proietta i rifiuti del burraco (nessuna `projectRefusalDetails` per `qlash.games.burraco.table.create`, router.js ~330-385 @ qlash-limulo
// origin/staging 3495ea9): alla TMA arriva `403 E_QLASH_HTTP_403` e basta, uguale a ogni altro 403. Letto dal CODICE, non misurato dal vivo. Per questo
// dopo il 403 di un tavolo `casual` (il solo che CONTA per la condotta, `TIPI_TAVOLO` di QBurraco `resolvers.js`) si rilegge la condotta e si dice cio' che il
// server HA. Se la rilettura non risponde, o non dice `sospensione`, resta la frase di prima (`vietato`): mai un'ora o un conto inventati.
//
// Forma (QBurraco @ 65e01ef7: `resolvers.js` `condottaRead`, `gamehost-core/src/service/routes.ts:357-363`, fixture `condotta.sospensione`):
//   { provider, data: { livello: 'nessuna' | 'avviso' | 'sospensione', punti, turniScaduti, abbandoni, finestraDal, sospesoFinoA: <ISO> | null, soglie } }
// busta di un game host: si scende con `capabilityPayloadOf`. La finestra e di 7 giorni (`rev24-condotta.test.ts`: «finestra 7 giorni»).

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** La rotta del BFF. Una GET: si ritenta come ogni lettura (una volta, solo rete/timeout/5xx). */
export const BURRACO_CONDOTTA_ROUTE = '/v1/games/burraco/condotta/mine';

export type LivelloCondotta = 'nessuna' | 'avviso' | 'sospensione';
const LIVELLI: readonly string[] = ['nessuna', 'avviso', 'sospensione'];

/**
 * Cio' che serve a dire la sospensione. `sospesoFinoA` in ms (null = non detto o non si legge), `abbandoni` e `turniScaduti` i conti della finestra (null = non detto).
 * I punti della condotta vengono da DUE fatti, non da uno: l'abbandono e il turno scaduto (peso 1, al massimo 3 per tavolo; qburraco-orc su #721): due tavoli con tre turni scaduti
 * ciascuno sospendono senza nessun abbandono, quindi la frase non puo dire «per abbandoni» e basta.
 */
export interface CondottaLetta {
  livello: LivelloCondotta;
  sospesoFinoA: number | null;
  abbandoni: number | null;
  turniScaduti: number | null;
}

/**
 * Lettura CHIUSA del payload gia sceso con `capabilityPayloadOf`: senza un `livello` dei tre noti la risposta non si legge (`null`, mai «nessuna» per difetto).
 * `sospesoFinoA` e `abbandoni` sono ACCESSORI: se mancano o non tornano valgono `null` e la frase salta quel pezzo.
 */
export function burracoCondottaOf(payload: unknown): CondottaLetta | null {
  const r = asRecord(payload);
  if (typeof r.livello !== 'string' || !LIVELLI.includes(r.livello)) return null;
  const fino = typeof r.sospesoFinoA === 'string' ? Date.parse(r.sospesoFinoA) : Number.NaN;
  const conto = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);
  return { livello: r.livello as LivelloCondotta, sospesoFinoA: Number.isFinite(fino) ? fino : null, abbandoni: conto(r.abbandoni), turniScaduti: conto(r.turniScaduti) };
}

/**
 * Le parti della frase di sospensione, o `null` se la condotta non dice `sospensione`. Cio' che non si puo dire in modo sicuro e un pezzo che MANCA (`null`), mai un valore
 * inventato: una fine gia passata (o uguale a `adesso`) non si dice, e «0 abbandoni» nemmeno (la sospensione puo venire dai turni scaduti: un conto a zero direbbe il falso).
 * `finoOggi`: la fine cade nello stesso giorno di `adesso` (nel fuso del telefono): solo allora basta l'orario, altrimenti la frase vuole anche la data.
 */
export interface Sospensione {
  fino: number | null;
  finoOggi: boolean;
  abbandoni: number | null;
  turniScaduti: number | null;
  /** PERCHE: i fatti che il server conta, mai piu di quelli (`condotta` = nessuno dei due conti e positivo o letto: si dice «per condotta», senza inventare la causa) */
  motivo: 'abbandoni' | 'turni' | 'entrambi' | 'condotta';
}

export function sospensioneDi(condotta: CondottaLetta | null, adesso: number): Sospensione | null {
  if (condotta === null || condotta.livello !== 'sospensione') return null;
  const fino = condotta.sospesoFinoA !== null && condotta.sospesoFinoA > adesso ? condotta.sospesoFinoA : null;
  const abbandoni = condotta.abbandoni !== null && condotta.abbandoni > 0 ? condotta.abbandoni : null;
  const turniScaduti = condotta.turniScaduti !== null && condotta.turniScaduti > 0 ? condotta.turniScaduti : null;
  const motivo = abbandoni !== null && turniScaduti !== null ? 'entrambi' : abbandoni !== null ? 'abbandoni' : turniScaduti !== null ? 'turni' : 'condotta';
  return { fino, finoOggi: fino !== null && stessoGiorno(fino, adesso), abbandoni, turniScaduti, motivo };
}

const stessoGiorno = (a: number, b: number): boolean => {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
};
